import OpenAI from 'openai';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * OpenAI Service for portfolio analysis and recommendations
 */
class OpenAIService {
  
  /**
   * Analyze portfolio data and generate AI-powered recommendations
   * @param {Object} portfolioData - Portfolio analytics data
   * @param {Array} trends - Price trend data
   * @param {Array} collections - Collection comparison data
   * @param {Array} topPerformers - Top performing items
   * @returns {Promise<Object>} AI-generated recommendations
   */
  static async generateRecommendations(portfolioData, trends, collections, topPerformers) {
    try {
      // Build context for AI analysis
      const context = this.buildAnalysisContext(portfolioData, trends, collections, topPerformers);
      
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { 
            role: 'system', 
            content: 'You are an expert collectibles portfolio advisor. Analyze portfolio data and provide actionable investment recommendations. Focus on ROI optimization, risk management, and market opportunities. Provide specific, data-driven insights.' 
          },
          { 
            role: 'user', 
            content: `Analyze this collectibles portfolio and provide recommendations:\n\n${context}\n\nProvide 5-7 specific recommendations covering: buy/sell timing, diversification opportunities, undervalued items, risk assessment, and market trends. Each recommendation should include a clear action, rationale, and expected impact.`
          },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'portfolio_recommendations',
            schema: {
              type: 'object',
              properties: {
                summary: { 
                  type: 'string',
                  description: 'Brief portfolio health summary (2-3 sentences)'
                },
                overallScore: {
                  type: 'number',
                  description: 'Portfolio health score from 0-100'
                },
                recommendations: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      type: { 
                        type: 'string',
                        enum: ['opportunity', 'warning', 'info', 'alert']
                      },
                      priority: {
                        type: 'string',
                        enum: ['high', 'medium', 'low']
                      },
                      action: { type: 'string' },
                      expectedImpact: { type: 'string' }
                    },
                    required: ['title', 'description', 'type', 'priority', 'action']
                  }
                },
                keyMetrics: {
                  type: 'object',
                  properties: {
                    diversificationScore: { type: 'number' },
                    riskLevel: { type: 'string' },
                    growthPotential: { type: 'string' }
                  },
                  required: ['diversificationScore', 'riskLevel', 'growthPotential']
                }
              },
              required: ['summary', 'overallScore', 'recommendations', 'keyMetrics'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'medium',
        verbosity: 'medium',
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      return { data: result, error: null };
      
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating recommendations:', error);
      }
      return { data: null, error: errorInfo?.message };
    }
  }

  /**
   * Analyze specific collection for optimization opportunities
   */
  static async analyzeCollection(collectionData, items) {
    try {
      const context = `Collection: ${collectionData?.name}\nType: ${collectionData?.type}\nTotal Items: ${items?.length}\nTotal Value: $${collectionData?.totalValue}\nROI: ${collectionData?.roi}%\n\nTop Items: ${items?.slice(0, 5)?.map(item => `${item?.name} - $${item?.current_value} (${item?.roi?.toFixed(1)}% ROI)`)?.join(', ')}`;
      
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { 
            role: 'system', 
            content: 'You are a collectibles expert. Analyze collection data and suggest optimization strategies.' 
          },
          { 
            role: 'user', 
            content: `Analyze this collection and provide 3-5 specific optimization recommendations:\n\n${context}`
          },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'collection_analysis',
            schema: {
              type: 'object',
              properties: {
                insights: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      action: { type: 'string' }
                    },
                    required: ['title', 'description', 'action']
                  }
                }
              },
              required: ['insights'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'minimal',
        verbosity: 'low',
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      return { data: result, error: null };
      
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error analyzing collection:', error);
      }
      return { data: null, error: errorInfo?.message };
    }
  }

  /**
   * Build analysis context from portfolio data
   */
  static buildAnalysisContext(portfolioData, trends, collections, topPerformers) {
    const context = [];
    
    // Portfolio overview
    if (portfolioData) {
      context?.push(`Portfolio Overview:`);
      context?.push(`- Total Value: $${portfolioData?.totalValue?.toLocaleString()}`);
      context?.push(`- Total Cost: $${portfolioData?.totalCost?.toLocaleString()}`);
      context?.push(`- Portfolio ROI: ${portfolioData?.portfolioROI?.toFixed(2)}%`);
      context?.push(`- Unrealized Gains: $${portfolioData?.unrealizedGains?.toLocaleString()}`);
      context?.push(`- Total Items: ${portfolioData?.totalItems}`);
      context?.push('');
    }

    // Price trends
    if (trends?.length > 0) {
      const recentTrend = trends?.slice(-5);
      const trendDirection = recentTrend?.[recentTrend?.length - 1]?.value > recentTrend?.[0]?.value ? 'upward' : 'downward';
      context?.push(`Recent Price Trends (${trendDirection}):`);
      recentTrend?.forEach(t => {
        context?.push(`- ${t?.date}: $${t?.value?.toLocaleString()} (ROI: ${t?.roi?.toFixed(1)}%)`);
      });
      context?.push('');
    }

    // Collection breakdown
    if (collections?.length > 0) {
      context?.push(`Collection Breakdown:`);
      collections?.slice(0, 5)?.forEach(col => {
        context?.push(`- ${col?.name} (${col?.type}): ${col?.itemCount} items, $${col?.totalValue?.toLocaleString()} value, ${col?.roi?.toFixed(1)}% ROI`);
      });
      context?.push('');
    }

    // Top performers
    if (topPerformers?.length > 0) {
      context?.push(`Top Performers:`);
      topPerformers?.slice(0, 5)?.forEach((item, idx) => {
        context?.push(`${idx + 1}. ${item?.name}: $${parseFloat(item?.current_value)?.toLocaleString()} (${item?.roi?.toFixed(1)}% ROI, +$${item?.gain?.toLocaleString()})`);
      });
    }

    return context?.join('\n');
  }
}

export default OpenAIService;
